import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { isSideBySide, leadCellOf, templateOfRow } from '../layout/layout';
import { DatasetRow, PRIMARY_ROW_INDEX } from '../layout/types';
import RowFooter from '../tile/RowFooter';
import {
    resolveRowBlend,
    resolveRowWindowing,
    resolveSeriesWindowing,
} from '../tile/tileLink';
import { RowHost } from './RowHost';

interface GridRowFooterProps {
    row: DatasetRow;
    rowIndex: number;
    host: RowHost;
}

/**
 * The footer of one bound dataset row, wired to the grid: the row's image,
 * the primary image's actions on the primary row, the image's window, the
 * host's controls of what it draws over the image, the fused image and the
 * blend, the row's shape and its place in the stack. Reads come from the row's series.
 * A typed or stepped window writes the row's series alone; a named preset
 * fans out per the fan-out policy. Nothing for a row with no dataset, or
 * whose dataset the adapter cannot resolve.
 */
export default function GridRowFooter({
    row,
    rowIndex,
    host,
}: GridRowFooterProps) {
    const volume = row.dataset
        ? host.datasetAdapter.resolve(row.dataset.seriesInstanceUid)
        : undefined;
    const lead = leadCellOf(row);
    if (!volume || !lead) {
        return null;
    }
    const seriesInstanceUid = volume.metadata.seriesInstanceUid;
    const isPrimary = rowIndex === PRIMARY_ROW_INDEX;
    const blend = resolveRowBlend(seriesInstanceUid, host.blendAdapter);
    // The primary row shows the primary image: a choice made on it asks for
    // that image as the primary image, and the row follows the answer, so
    // the image a row shows with no primary image named can be made the
    // primary image too. Another row rebinds only to another image: a pick
    // of the image it shows changes nothing, and on a maximized tile the
    // rebind would restore the grid for nothing.
    const onBindRowImage = isPrimary
        ? host.primaryImage.choose
        : (uid: SeriesInstanceUid) => {
              if (uid !== seriesInstanceUid) {
                  host.binding.requestBind(rowIndex, uid);
              }
          };
    return (
        <RowFooter
            layout={{
                shape: templateOfRow(row),
                onSelect: (protocol) =>
                    host.onRehangRow(rowIndex, protocol.template),
            }}
            rowImage={volume.metadata}
            availableDatasets={host.binding.availableDatasets}
            onBindRowImage={onBindRowImage}
            overlayControls={host.rowOverlayControls?.({ seriesInstanceUid })}
            rowWindowing={resolveRowWindowing(
                host.windowLevelTargets,
                lead.id,
                seriesInstanceUid,
                host.windowingAdapter
            )}
            fusedWindowingFor={(uid) =>
                resolveSeriesWindowing(uid, host.windowingAdapter)
            }
            blendSource={blend.source}
            onFuse={blend.onFuse}
            onClear={blend.onClear}
            onSetBlend={blend.onSetBlend}
            sideBySide={isSideBySide(row)}
            onToggleSideBySide={() => host.onToggleSideBySide(rowIndex)}
            onAddRowBelow={() => host.onAddRowBelow(rowIndex)}
            onCloseRow={() => host.onCloseRow(rowIndex)}
            canClose={host.canCloseRow && !isPrimary}
            isPrimary={isPrimary}
        />
    );
}
